const CONSENT_STORAGE_KEY = "duffloo_analytics_consent";

const GTM_ID = "GTM-5DKMXXFK";
const CLARITY_ID = "jy9oz8l2fo";
const TAWK_SRC = "https://embed.tawk.to/66de31bf50c10f7a00a5f933/1i79v4kq4";
// T-01: Meta (Facebook) Pixel for www.duffloo.com. Hard-coded to match GTM_ID
// and CLARITY_ID above — a pixel id is a public identifier, not a secret, and
// keeping all four tag ids in this one file is what makes "every tag is
// consent-gated" checkable by reading a single module.
const META_PIXEL_ID = "1739797150594673";

// Consent cookie lives on .duffloo.com so app.duffloo.com shares the choice.
const CONSENT_COOKIE_MAX_AGE = 15552000; // 180 days

// C-03: PIPEDA principle 4.3.8 — withdrawal must be as easy as giving consent.
// The footer "Cookie Preferences" control dispatches this event; the banner
// listens for it and re-opens so the visitor can change their mind.
export const CONSENT_REOPEN_EVENT = "duffloo:cookie-consent:reopen";

let analyticsLoaded = false;

const consentCookieDomain = () =>
  window.location.hostname.endsWith("duffloo.com") ? "; domain=.duffloo.com" : "";

// A-03 — VERIFICATION_PLAN.md Phase A checks that accepting on one domain means
// no re-prompt on the other. It FAILED, and not because of the cookie domain:
// this site and app.duffloo.com both write `duffloo_analytics_consent` on
// .duffloo.com, but this side writes JSON {"accepted":bool,"at":ISO} while the
// customer app writes the bare string 'accepted' | 'declined'. JSON.parse of
// "accepted" throws, so this reader returned null, fell back to per-origin
// localStorage, and re-prompted — and each domain's write clobbered the other's
// record, since the name, domain and path all match.
//
// Fixed by accepting BOTH encodings on BOTH sides (mirror in
// DufflooCustomerApp/src/services/analyticsConsent.web.js). The readers were
// changed rather than the writers because cookies already in real browsers are
// in the old formats; normalizing only the writer would still re-prompt them.
const normalizeConsentValue = (raw) => {
  if (!raw) {
    return null;
  }
  // The customer app's bare-string form. No timestamp is available; `at` is
  // never read on this site, and the local record keeps its own.
  if (raw === "accepted" || raw === "declined") {
    return { accepted: raw === "accepted" };
  }
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed.accepted === "boolean" ? parsed : null;
  } catch {
    return null;
  }
};

const readConsentCookie = () => {
  try {
    const row = document.cookie
      .split("; ")
      .find((entry) => entry.startsWith(`${CONSENT_STORAGE_KEY}=`));
    if (!row) {
      return null;
    }
    return normalizeConsentValue(
      decodeURIComponent(row.slice(CONSENT_STORAGE_KEY.length + 1))
    );
  } catch {
    return null;
  }
};

const writeConsentCookie = (value) => {
  try {
    document.cookie = `${CONSENT_STORAGE_KEY}=${encodeURIComponent(
      JSON.stringify(value)
    )}${consentCookieDomain()}; path=/; max-age=${CONSENT_COOKIE_MAX_AGE}; SameSite=Lax`;
  } catch {
    // Cookie write failures must never break the page.
  }
};

export const getStoredAnalyticsConsent = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const fromCookie = readConsentCookie();
  if (fromCookie) {
    return fromCookie;
  }

  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const setStoredAnalyticsConsent = (accepted) => {
  if (typeof window === "undefined") {
    return;
  }

  const value = {
    accepted: !!accepted,
    at: new Date().toISOString(),
  };
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(value));
  writeConsentCookie(value);
};

/** True when tags were injected in THIS page session (see loadAnalyticsScripts). */
export const analyticsScriptsLoaded = () => analyticsLoaded;

/** Ask the banner to re-open so a stored choice can be changed. */
export const requestConsentReopen = () => {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(new Event(CONSENT_REOPEN_EVENT));
};

// Google Tag Manager (GTM-5DKMXXFK) is the single tag on the site. It loads
// GA4 (G-WEWPNGXLC8), the Google Ads Conversion Linker and the Ads conversion
// tags. Loaded only after the visitor accepts the cookie banner; Consent Mode
// v2 signals are granted at that point.
const loadGoogleTagManager = () => {
  if (typeof window === "undefined" || document.getElementById("duffloo-gtm")) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  // Consent Mode v2: granted because this loader only runs post-acceptance.
  // If tags ever load pre-consent, default these to "denied" and update on
  // acceptance instead. gtag must push the `arguments` object (not an array
  // literal) or GTM's consent APIs ignore the command.
  function gtag() {
    window.dataLayer.push(arguments);
  }
  gtag("consent", "default", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
  });
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });

  const script = document.createElement("script");
  script.id = "duffloo-gtm";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
};

const loadClarity = () => {
  if (typeof window === "undefined" || window.clarity) {
    return;
  }

  (function (c, l, a, r, i, t, y) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    t = l.createElement(r);
    t.async = 1;
    t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", CLARITY_ID);

  // Clarity only runs post-acceptance, so signal consent explicitly.
  window.clarity && window.clarity("consent");
};

/**
 * T-01 · Meta Pixel.
 *
 * Loaded HERE and not as a raw snippet in gatsby-ssr.js. A snippet in the SSR
 * document head executes on every page load before the visitor has answered the
 * cookie banner, which is precisely the pre-consent tracking the banner exists
 * to prevent (CASL/PIPEDA) — and Meta's pixel drops identifiers and fires a
 * PageView the moment it loads, so there is no benign window.
 *
 * There is deliberately NO <noscript> fallback image. The standard one is an
 * <img> that requests Meta unconditionally, and being markup it cannot be
 * consent-gated at all — it would reintroduce exactly the pre-consent hit this
 * placement avoids, for the small minority of visitors with JS disabled.
 */
const loadMetaPixel = () => {
  if (typeof window === "undefined" || window.fbq) {
    return;
  }

  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    "script",
    "https://connect.facebook.net/en_US/fbevents.js"
  );
  /* eslint-enable */

  // Explicit grant, mirroring the Clarity/GTM consent signals below and above:
  // this loader only runs post-acceptance, so the grant is accurate. If the
  // pixel ever loads earlier, revoke here and grant on acceptance instead.
  window.fbq("consent", "grant");
  window.fbq("init", META_PIXEL_ID);
  window.fbq("track", "PageView");
};

/**
 * W-01 / P4-07 · routes that must NOT get the tawk.to widget.
 *
 * Everything under /washer/app/ is the signed-in Washer Hero portal, and it has
 * its own first-party support chat (components/washerPortal/WasherSupportChat)
 * that is keyed to the Hero's authenticated uid and reaches Duffloo staff in the
 * super app. Loading tawk there produced two problems at once:
 *
 *   1. TWO competing chat launchers on the same screen, one of which (tawk) has
 *      no idea who the Hero is and lands in a different inbox.
 *   2. tawk is pinned z-index 99 (styles/base/_base.scss). The training's
 *      BottomNav is z-50 and the Duffloo chat is z-90, so tawk painted over
 *      BOTH — and its bubble sits bottom-right, which is exactly where the
 *      rightmost "Progress" tab is. The tab was unclickable.
 *
 * Deliberately scoped to /washer/app/ and not to /washer-login/ or
 * /washer-registration/: those are pre-signup pages with no Hero identity yet,
 * where the public tawk queue is the correct place for a question. The rule the
 * operator asked for — the Hero support chat wherever a signed-up Hero is
 * involved — is exactly the authenticated portal.
 */
export const isFirstPartyChatRoute = (pathname) =>
  typeof pathname === "string" && pathname.startsWith("/washer/app/");

const currentPath = () =>
  typeof window === "undefined" ? "" : window.location.pathname;

/** tawk's own mobile breakpoint; used only to scope the minimize-on-nav below. */
const MOBILE_MAX_WIDTH = 768;

const loadTawk = () => {
  if (typeof window === "undefined" || document.getElementById("duffloo-tawk")) {
    return;
  }

  window.Tawk_API = window.Tawk_API || {};
  window.Tawk_LoadStart = new Date();
  /**
   * P4-07: this was `{zIndex: 99}` and nothing else — no placement, no
   * per-breakpoint config — so the widget kept tawk's defaults: bottom-right on
   * every screen, and a maximized panel that fills the mobile viewport.
   *
   * `visibility` is the only placement control the embed API exposes. On mobile
   * the launcher is moved to the bottom-LEFT (same side as the scroll-to-top
   * control). The scroll button is lifted above the bubble via
   * components/scrollToTop/_scrollToTop.scss — keep its `$tawk-mobile-y-offset`
   * in sync with `yOffset` below. The cookie banner, when visible, runs the
   * full width along the bottom.
   *
   * WHAT THIS DOES NOT FIX (do not claim otherwise): the size and layout of the
   * MAXIMIZED panel on mobile, and the absence of a close control inside it.
   * Both live inside tawk's cross-origin iframe and are set from the tawk
   * dashboard — property 66de31bf50c10f7a00a5f933, widget 1i79v4kq4. No embed
   * option and no stylesheet in this repo can reach them. The mitigation code
   * CAN provide is syncTawkForRoute() below, which minimizes the panel on
   * navigation so a visitor trapped behind a full-screen panel can escape it by
   * moving to another page.
   */
  window.Tawk_API.customStyle = {
    // 2026-09-19: was 99 — BELOW the header (z 1000 on mobile). When tawk
    // maximized to full screen on a phone, the header covered the panel's top
    // bar, which holds its only minimize control: the visitor could not get
    // out ("no way to exit the chat"). Measured: elementFromPoint at the top
    // of the maximized panel returned our header's logo. 1050 puts the chat
    // above the header and still below the arrival modal (1100) and the
    // cookie banner (1200).
    zIndex: 1050,
    visibility: {
      desktop: { position: "br", xOffset: 20, yOffset: 20 },
      mobile: { position: "bl", xOffset: 10, yOffset: 80 },
      bubble: { rotate: "0deg", xOffset: -20, yOffset: -20 },
    },
  };
  // The widget can finish loading after a client-side navigation has already
  // happened, so re-apply the route rule once it is actually there.
  window.Tawk_API.onLoad = function onTawkLoad() {
    syncTawkForRoute(currentPath());
  };

  const script = document.createElement("script");
  script.id = "duffloo-tawk";
  script.async = true;
  script.src = TAWK_SRC;
  script.charset = "UTF-8";
  script.setAttribute("crossorigin", "*");
  document.body.appendChild(script);
};

/**
 * Apply the per-route tawk rules. Safe to call on every route change.
 *
 * Called from gatsby-browser's onRouteUpdate. Every call is guarded: the tawk
 * API object exists before the widget itself does, so the methods can legitimately
 * be missing, and a chat widget must never be able to throw into navigation.
 */
export const syncTawkForRoute = (pathname) => {
  if (typeof window === "undefined") {
    return;
  }

  if (isFirstPartyChatRoute(pathname)) {
    // Hide rather than never-load, because a visitor can reach the portal from
    // a public page where tawk is already on the page.
    try {
      window.Tawk_API?.hideWidget?.();
    } catch {
      // A chat widget must not break navigation.
    }
    return;
  }

  // Coming back OUT of the portal: the visitor consented, so tawk belongs here.
  // It may never have been injected (first landing was a portal route), in which
  // case load it now rather than leaving public pages permanently chat-less.
  if (analyticsLoaded && !document.getElementById("duffloo-tawk")) {
    loadTawk();
    return;
  }

  try {
    window.Tawk_API?.showWidget?.();
    // P4-07: the escape hatch for the mobile takeover. Scoped to mobile widths
    // so a desktop visitor mid-conversation does not have their panel collapsed
    // out from under them on every internal link.
    if (window.innerWidth <= MOBILE_MAX_WIDTH) {
      window.Tawk_API?.minimize?.();
    }
  } catch {
    // As above.
  }
};

export const loadAnalyticsScripts = () => {
  if (typeof window === "undefined" || analyticsLoaded) {
    return;
  }

  analyticsLoaded = true;
  loadGoogleTagManager();
  loadClarity();
  loadMetaPixel();
  // W-01: never inject tawk onto a portal route in the first place — hiding it
  // after the fact still costs the request and still flashes the bubble.
  if (!isFirstPartyChatRoute(currentPath())) {
    loadTawk();
  }
};

export const initAnalyticsIfConsented = () => {
  const consent = getStoredAnalyticsConsent();
  if (consent?.accepted) {
    loadAnalyticsScripts();
  }
};
